import React, { useContext, useEffect, useState } from 'react';
import styled, { ThemeContext } from 'styled-components';
import { NextPage, GetServerSideProps } from 'next';

import { H2, H2Styles, H3Styles, H4Styles, H5, P1, S1 } from '../components/Typography';
import { getPropsForPageLoad } from '../utils/pageLoad';

import config from '../../config.json';
import Head from '../components/Head';
import { getFeaturedCollections } from '../api/storefront/product';
import CreatorScroller from '../components/CreatorScroller';
import { ShopPageWrapper } from './shop/[collection]';
import CollectionsDisplay from '../components/pages/featured-creators/CollectionsDisplay';
import { getCollectionByHandle } from '../api/collections';
import { findCollectionByMetafield, parseMetafields } from '../utils/product';
import CreatorsDisplay from '../components/CreatorsDisplay';
import { useWindowSize } from '../hooks/useWindowSize';
import CreatorGrid from '../components/CreatorGrid';
import Container from '../components/Container';

const { pagination, collections } = config;

const LiveSectionWrapper = styled(ShopPageWrapper)`
  padding-bottom: 0;
`;

const ShopPageH2 = styled(H2)`
  ${H4Styles};
  text-transform: uppercase;
  margin-bottom: 24px;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.headerBreakpoint}px) {
    ${H2Styles};
    margin-bottom: 30px;
  }
`;

const ShopPageContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const CollectionCarouselWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CollectionsDisplayWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CreatorsDisplayTitle = styled(H5)`
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.headerBreakpoint}px) {
    ${H3Styles}
  }
`;

const DefaultTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${({ theme }) => theme.colors.neutral2};
  padding-bottom: 30px;
  gap: 16px;
  margin-bottom: 30px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.headerBreakpoint}px) {
    gap: 20px;
    margin-bottom: 88px;
    padding-bottom: 74px;
  }
`;

const Subheading = styled(S1)`
  font-size: 20px;
  margin-bottom: 10px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.headerBreakpoint}px) {
    margin-bottom: 18px;
  }
`;

const CollectionSectionTitle = styled(H5)`
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.headerBreakpoint}px) {
    font-size: 36px;
  }
`;

const CollectionCarouselTextWrapper = styled.div`
  margin-top: 52px;
  display: flex;
  justify-content: flex-start;
  margin-left: 16px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.largeTablet}px) {
    margin-left: 64px;
  }
`;

const FullWidthContainer = styled(Container)`
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.maxContent}px) {
    display: block;
    width: 100%;
  }
`;

type FeaturedCollectionsPageProps = {
  featuredCollections: FeaturedCollectionsResponse | null;
  defaultFeaturedCollections: Shopify.CollectionEdge[] | null;
};

const FeaturedCollectionsPage: NextPage<FeaturedCollectionsPageProps> = ({
  featuredCollections,
  defaultFeaturedCollections,
}: FeaturedCollectionsPageProps) => {
  const { routes } = config;
  const [isMobileViewport, setIsMobileViewport] = useState<boolean>(false);
  const size = useWindowSize();
  const theme = useContext(ThemeContext);
  const [isMaxViewport, setIsMaxViewport] = useState<boolean>(false);

  useEffect(() => {
    if (!size.width) {
      return;
    }
    // eslint-disable-next-line react/destructuring-assignment
    const isMobileViewport = size.width < theme.breakpoints.desktop;
    setIsMobileViewport(isMobileViewport);
  }, [size]);

  useEffect(() => {
    if (!size.width) {
      return;
    }
    // eslint-disable-next-line react/destructuring-assignment
    const isMaxViewport = size.width >= theme.breakpoints.maxContent;
    if (isMaxViewport) {
      setIsMaxViewport(true);
    } else {
      setIsMaxViewport(false);
    }
  }, [size]);

  useEffect(() => {
    if ((window as any).drift && (window as any).drift.api) {
      (window as any).drift.api.widget.hide();
    }
    return () => {
      if ((window as any).drift && (window as any).drift.api) {
        (window as any).drift.api.widget.show();
      }
    };
  }, []);

  return (
    <>
      <Head
        title="Featured Creators"
        description="Discover the latest and greatest products from your favorite Creators. Explore our featured collections and find unique items designed by the community."
        url={routes.featuredCreators}
      />
      <LiveSectionWrapper>
        <ShopPageContentWrapper>
          <ShopPageH2 data-testid="collection-title">Featured Creators</ShopPageH2>
          {!featuredCollections && defaultFeaturedCollections && defaultFeaturedCollections.length > 0 && (
            <>
              <DefaultTextWrapper>
                <CreatorsDisplayTitle>Hmm, things seem to be taking longer than usual 🤔</CreatorsDisplayTitle>
                <P1>
                  We&apos;re having a bit of trouble loading this page. Hang tight—your favorite creators&apos; merch
                  will be back shortly!
                </P1>
              </DefaultTextWrapper>
              <>
                <Subheading>For now, check out these creators:</Subheading>
                <CreatorsDisplay creators={defaultFeaturedCollections} titleForTracking="default_featured_creators" />
              </>
            </>
          )}
          {featuredCollections?.live && featuredCollections.live.length > 0 && (
            <CollectionsDisplayWrapper>
              <CollectionSectionTitle>The Latest Drops</CollectionSectionTitle>
              <CollectionsDisplay creatorCollections={featuredCollections.live} sectionName="latest_drops" />
            </CollectionsDisplayWrapper>
          )}
        </ShopPageContentWrapper>
      </LiveSectionWrapper>
      {featuredCollections?.comingSoon && featuredCollections.comingSoon.length > 0 && (
        <CollectionCarouselWrapper>
          {isMaxViewport ? (
            <FullWidthContainer>
              <CollectionCarouselTextWrapper>
                <CollectionSectionTitle>A Sneak Peek...</CollectionSectionTitle>
              </CollectionCarouselTextWrapper>
            </FullWidthContainer>
          ) : (
            <CollectionCarouselTextWrapper>
              <CollectionSectionTitle>A Sneak Peek...</CollectionSectionTitle>
            </CollectionCarouselTextWrapper>
          )}
          {isMobileViewport ? (
            <CreatorScroller
              creatorCollections={featuredCollections.comingSoon}
              isComingSoon
              sectionName="sneak_peek"
            />
          ) : isMaxViewport ? (
            <FullWidthContainer>
              <CreatorGrid creatorCollections={featuredCollections.comingSoon} sectionName="sneak_peek" isComingSoon />
            </FullWidthContainer>
          ) : (
            <CreatorGrid creatorCollections={featuredCollections.comingSoon} sectionName="sneak_peek" isComingSoon />
          )}
        </CollectionCarouselWrapper>
      )}
      {featuredCollections?.past && featuredCollections.past.length > 0 && (
        <CollectionCarouselWrapper>
          {isMaxViewport ? (
            <FullWidthContainer>
              <CollectionCarouselTextWrapper>
                <CollectionSectionTitle>Discover Your New Fave!</CollectionSectionTitle>
              </CollectionCarouselTextWrapper>
            </FullWidthContainer>
          ) : (
            <CollectionCarouselTextWrapper>
              <CollectionSectionTitle>Discover Your New Fave!</CollectionSectionTitle>
            </CollectionCarouselTextWrapper>
          )}
          {isMobileViewport ? (
            <CreatorScroller creatorCollections={featuredCollections.past} sectionName="discover_new_fave" />
          ) : isMaxViewport ? (
            <FullWidthContainer>
              <CreatorGrid creatorCollections={featuredCollections.past} sectionName="discover_new_fave" />
            </FullWidthContainer>
          ) : (
            <CreatorGrid creatorCollections={featuredCollections.past} sectionName="discover_new_fave" />
          )}
        </CollectionCarouselWrapper>
      )}
    </>
  );
};

export const getServerSideProps: GetServerSideProps<FeaturedCollectionsPageProps> = async (ctx) => {
  const propsForPageLoad = await getPropsForPageLoad(ctx);

  if (!propsForPageLoad) {
    return {
      notFound: true,
    };
  }

  let featuredCollections = null;
  let defaultFeaturedCollections = null;

  try {
    featuredCollections = await getFeaturedCollections('endingSoon').then((response) => response.data);
  } catch (error) {
    // Handle the fallback if the getFeaturedCollections fails
    const productResults = await getCollectionByHandle(
      collections.topCampaigns,
      undefined,
      1,
      pagination.carousel,
    ).then((data) => data?.products.edges as Shopify.ProductEdge[]);

    const seenCreatorNames = new Set();
    const uniqueCreators = productResults.filter((product) => {
      const metafields = parseMetafields(product.node.metafields);
      const { creator } = metafields;

      if (seenCreatorNames.has(creator)) {
        return false;
      }

      seenCreatorNames.add(creator);
      return true;
    });

    const creatorCollection =
      uniqueCreators
        .slice(0, 3)
        .map((product) => {
          const collection = findCollectionByMetafield(product.node, 'isCreatorCollection', 'true');
          return collection || null;
        })
        .filter((collection) => collection !== null) || [];

    defaultFeaturedCollections = creatorCollection as Shopify.CollectionEdge[];
  }

  return {
    props: {
      featuredCollections,
      defaultFeaturedCollections,
      ...propsForPageLoad,
    },
  };
};

export default FeaturedCollectionsPage;
